import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Space } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { RootState, AppDispatch } from '../../redux/store';
import { fetchDeviceTypes } from '../../redux/DeviceTypes';
import { DeviceType } from './types';
import { DeviceTypeList } from './DeviceTypeList';
import { DeviceTypeForm } from './DeviceTypeForm';
import { StatsCards } from './StatsCards';
import CustomInput from '../../commonComponents/input';
import {
  Container,
  HeaderSection,
  Title,
  Subtitle,
  StyledCard,
  TopControlBar,
  AddButton,
} from './styles';
import { LABELS, PLACEHOLDERS } from './constants';
import { ThemeProvider } from 'styled-components';

const DeviceTypes: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { deviceTypes, loading } = useSelector((state: RootState) => state.deviceTypes);
  const { theme } = useSelector((state: RootState) => state.theme);
  const themeMode = theme === 'dark' ? 'dark' : 'light';
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentDeviceType, setCurrentDeviceType] = useState<DeviceType | null>(null);
  const [filteredDeviceTypes, setFilteredDeviceTypes] = useState<DeviceType[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    dispatch(fetchDeviceTypes());
  }, [dispatch]);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredDeviceTypes(deviceTypes);
    } else {
      const filtered = deviceTypes.filter((item) =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredDeviceTypes(filtered);
    }
  }, [deviceTypes, searchTerm]);

  const handleAddClick = () => {
    setCurrentDeviceType(null);
    setIsModalOpen(true);
  };

  const handleEditClick = (deviceType: DeviceType) => {
    setCurrentDeviceType(deviceType);
    setIsModalOpen(true);
  };

  const handleSave = () => {
    setIsModalOpen(false);
    setCurrentDeviceType(null);
    dispatch(fetchDeviceTypes());
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setCurrentDeviceType(null);
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  return (
    <ThemeProvider theme={{ mode: themeMode }}>
      <Container>
        <HeaderSection>
          <div>
            <Title level={2} style={{ margin: 0 }}>
              {LABELS.DEVICE_TYPE_LIST}
            </Title>
            <Subtitle>
              Manage and configure different types of devices in your system
            </Subtitle>
          </div>
          <Space>
            <AddButton
              type="primary"
              icon={<FontAwesomeIcon icon={faPlus} />}
              onClick={handleAddClick}
            >
              {LABELS.ADD_DEVICE_TYPE}
            </AddButton>
          </Space>
        </HeaderSection>

        <StatsCards
          totalCount={deviceTypes.length}
          filteredCount={filteredDeviceTypes.length}
          loading={loading}
        />

        <StyledCard>
          <TopControlBar>
            <CustomInput
              placeholder={PLACEHOLDERS.SEARCH}
              value={searchTerm}
              onChange={handleSearch}
              allowClear
              width="300px"
            />
          </TopControlBar>
          <DeviceTypeList
            deviceTypes={filteredDeviceTypes}
            onEdit={handleEditClick}
            onAdd={handleAddClick}
          />
        </StyledCard>
      </Container>

      <DeviceTypeForm
        isOpen={isModalOpen}
        initialDeviceType={currentDeviceType}
        onSave={handleSave}
        onCancel={handleCancel}
      />
    </ThemeProvider>
  );
};

export default DeviceTypes;
